import type { FC, MouseEventHandler } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import type { IUserCommunication, UserCommunicationButton } from "@/Components/Api/CommonServiceOutput";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { browserStatics } from "@/src/shared/BrowserStatic";
import TelemetryButton from "@Components/Button/TelemetryButton/TelemetryButton";
import ImageLoader from "@Components/ImageLoader";
import type { IModal } from "@Components/Modal";
import { Modal } from "@Components/Modal";
import { gotoNativeScreen } from "@Components/Modules/Helpers";

import * as css from "./UserCommunicationModal.module.css";

export interface IUserCommunicationModal extends Omit<IModal, "isOpen">, IUserCommunication {
    customActionClick?: (action: number) => string | undefined;
}

const UserCommunicationModal: FC<IUserCommunicationModal> = (props) => {
    const navigate = useNavigate();
    useEffect(() => {
        if (props.telemetryDisplayName)
            sendTelemetryEvent({ eventName: props.telemetryDisplayName, actionType: "Display" });
    }, []);

    const buttonClick = (
        buttonProps: UserCommunicationButton,
    ): MouseEventHandler<HTMLButtonElement> | undefined => {
        if (!props.customActionClick) {
            return undefined;
        }

        return (event) => {
            const result = props.customActionClick(buttonProps.buttonActionType);

            if (!result) {
                console.error(`Button with action value ${buttonProps.buttonActionType} does not exist`);
                return undefined;
            }

            if (browserStatics.isMobileApp) {
                const willAppHandleNavigation = gotoNativeScreen(result);
                if (willAppHandleNavigation) {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
            }

            navigate(result);
            event.preventDefault();
            event.stopPropagation();

            return false;
        };
    };

    const onClose = () => {
        if (!props.customActionClick) {
            return props.onClose;
        }

        return () => {
            const result = props.customActionClick(props.closeActionType);

            if (!result) {
                console.error(`Button with action value ${props.closeActionType} does not exist`);
                return;
            }

            if (browserStatics.isMobileApp) {
                const willAppHandleNavigation = gotoNativeScreen(result);
                if (willAppHandleNavigation) return;
            }

            navigate(result);
        };
    };

    return (
        <Modal {...props} className={css.userCommunicationModalComponent} onClose={onClose()} isOpen>
            {props.title && <p className={css.title}>{props.title}</p>}
            {props.imageUrl && <ImageLoader src={props.imageUrl} alt="Information Modal" />}
            {props.message && <p className={css.text}>{props.message}</p>}
            {props.buttons?.map((button, index) => (
                <TelemetryButton
                    key={index}
                    appearance="magentaRoundAppearance"
                    onClick={buttonClick(button)}
                    telemetryUrl={button.telemetryClickName}
                >
                    {button.text}
                </TelemetryButton>
            ))}
        </Modal>
    );
};

export default UserCommunicationModal;
